import {
  ArrowUpRight,
  BrainCircuit,
  Code2,
  Compass,
  GraduationCap,
  HeartPulse,
  Palette,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import type { PropsWithChildren, ReactNode } from "react";
import { Link } from "react-router-dom";

const iconMap = {
  code: Code2,
  brain: BrainCircuit,
  education: GraduationCap,
  wellbeing: HeartPulse,
  compass: Compass,
  design: Palette,
  sparkles: Sparkles,
} as const;

export function AppIcon({ name, size = 24 }: { name: string; size?: number }) {
  const Icon = iconMap[name as keyof typeof iconMap] || Sparkles;
  return <Icon size={size} strokeWidth={1.7} />;
}

interface ButtonProps extends PropsWithChildren {
  href?: string;
  variant?: "primary" | "secondary" | "ghost";
  className?: string;
  type?: "button" | "submit";
  disabled?: boolean;
  onClick?: () => void;
}

export function Button({
  children,
  href,
  variant = "primary",
  className = "",
  type = "button",
  disabled,
  onClick,
}: ButtonProps) {
  const classes = `button button-${variant} ${className}`.trim();
  if (href) {
    const internal = href.startsWith("/");
    const opensNewTab = href.startsWith("http://") || href.startsWith("https://");
    return internal ? (
      <Link className={classes} to={href}>
        {children}
      </Link>
    ) : (
      <a
        className={classes}
        href={href}
        target={opensNewTab ? "_blank" : undefined}
        rel={opensNewTab ? "noreferrer" : undefined}
      >
        {children}
      </a>
    );
  }
  return (
    <button className={classes} type={type} disabled={disabled} onClick={onClick}>
      {children}
    </button>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="section-heading" data-scroll-reveal="up">
      <div>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h2>{title}</h2>
      </div>
      <div className="section-heading-aside">
        {description && <p>{description}</p>}
        {action}
      </div>
    </div>
  );
}

export function LoadingState({ label = "Loading Steria House" }: { label?: string }) {
  return (
    <div className="state-page">
      <div className="loader-orbit">
        <span />
      </div>
      <p>{label}</p>
    </div>
  );
}

export function ErrorState({
  title = "We could not load this page",
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="state-page">
      <span className="state-icon">
        <RefreshCw size={24} />
      </span>
      <h1>{title}</h1>
      <p>{message}</p>
      {onRetry && <Button onClick={onRetry}>Try again</Button>}
    </div>
  );
}

export function TextLink({ to, children }: PropsWithChildren<{ to: string }>) {
  return (
    <Link className="text-link" to={to}>
      {children} <ArrowUpRight size={16} />
    </Link>
  );
}
